import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import toast from "react-hot-toast";
import Box from '../../components/Box';
import { KTCard } from '../../_metronic/helpers/components/KTCard';
import { KTCardBody } from '../../_metronic/helpers/components/KTCardBody';
import Content from '../../_metronic/layout/components/content/Content';
import { PageTitle } from '../../_metronic/layout/core';
import { ToolbarWrapper } from '../../_metronic/layout/components/toolbar';
import { storeActivityIndicator, storeAreYouSureModalCallback } from '../../redux/reducers/authSlice';
import { RootState } from '../../redux/store';
import RefreshButton from '../../components/RefreshButton';
import CustomSingleSelect, { CustomSingleSelectModel } from '../../components/CustomSingleSelect';
import { MesaiNoktasiPoSatiri, MesaiNoktasiService } from '../../services/MesaiNoktasiService';
import { AtananMesai, BekleyenMesai, MesaiAtamaService } from '../../services/MesaiAtamaService';
import { MesaiSiparisi, ondalikSaat, saatDakika, trTarih } from '../../services/MesaiKaydiService';
import { bolumSec, bolumSecenekleri, KademeSecimi, musteriSec, musteriSecenekleri, poNoSecenekleri, poSec, poSecenekleri } from '../../helpers/poKademe';

/** Atama satırı: Müşteri → Bölüm → PO → PO No zinciri tek bir PO Eşleştirme satırına (companyPoId) çözülür (ortak: helpers/poKademe). */
interface AtamaSatiri extends KademeSecimi {
    anahtar: string
    saat: string
}

let anahtarSayaci = 1;
const yeniAnahtar = () => 'atama-' + (anahtarSayaci++);

/** Saat metni → sayı (virgül de kabul). */
const saatSayi = (s: string) => {
    var n = Number((s ?? '').toString().replace(',', '.'));
    return isNaN(n) ? 0 : n;
}

const ceyrek = (n: number) => Math.round(n * 4) / 4;
const ceyrekMi = (n: number) => Math.abs(n * 4 - Math.round(n * 4)) < 0.0001;

/**
 * Atanacak Mesailer (Kargo Modülü > Nakliye, QR Mesai Faz 1b, 11.10.2026).
 * Bütün Lojistik ek mesai havuza düşer (QR noktasının otomatik fatura ataması kullanılmıyor).
 * Yetkili bekleyen kaydı seçer, saatleri Müşteri → Bölüm → PO → PO No satırlarına 0,25 adımla böler, "Ata ve sipariş oluştur".
 */
const MesaiAtama = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const auth = useSelector((state: RootState) => state.auth)
    const pageName = "Kargo Modülü > Nakliye > Atanacak Mesailer"
    const menu = auth.user.user?.data.authorizedMenus.find(x => x.menuName == pageName);

    const [bekleyenler, setBekleyenler] = useState<BekleyenMesai[]>([]);
    const [atananlar, setAtananlar] = useState<AtananMesai[]>([]);
    const [poSatirlari, setPoSatirlari] = useState<MesaiNoktasiPoSatiri[]>([]);
    const [seciliId, setSeciliId] = useState<string | undefined>(searchParams.get('id') ?? undefined);
    const [satirlar, setSatirlar] = useState<AtamaSatiri[]>([]);
    const [baslangic, setBaslangic] = useState<string>(moment().startOf('month').format('YYYY-MM-DD'));
    const [bitis, setBitis] = useState<string>(moment().endOf('month').format('YYYY-MM-DD'));

    const secili = bekleyenler.find(x => x.mesaiKaydiId == seciliId);
    const hedef = secili ? secili.siparisDk / 60 : 0;

    useEffect(() => {
        getPoSatirlari();
        getBekleyenler();
    }, [])

    useEffect(() => {
        getAtananlar();
    }, [baslangic, bitis])

    // Seçili kayıt değişince atama taslağı tek satır, tüm saat olarak açılır.
    useEffect(() => {
        setSatirlar(secili ? [{ anahtar: yeniAnahtar(), saat: String(ceyrek(secili.siparisDk / 60)) }] : []);
    }, [seciliId, secili?.siparisDk])

    const getPoSatirlari = async () => {
        try {
            var response = await MesaiNoktasiService.poSatirlari();

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            setPoSatirlari(response.data ?? []);
        } catch (error: any) {
            toast.error(error);
        }
    }

    const getBekleyenler = async () => {
        try {
            dispatch(storeActivityIndicator(true));

            var response = await MesaiAtamaService.bekleyenler();

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            var liste = response.data ?? [];
            setBekleyenler(liste);

            // Seçili kayıt artık bekleyenlerde yoksa (atandı) ilk kayıt seçilir.
            setSeciliId(onceki => onceki && liste.some(x => x.mesaiKaydiId == onceki) ? onceki : liste[0]?.mesaiKaydiId);
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const getAtananlar = async () => {
        try {
            if (!baslangic || !bitis || baslangic > bitis)
                return;

            var response = await MesaiAtamaService.atananlar(baslangic, bitis);

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            setAtananlar(response.data ?? []);
        } catch (error: any) {
            toast.error(error);
        }
    }

    const yenile = () => {
        getPoSatirlari();
        getBekleyenler();
        getAtananlar();
    }

    // ---------------------------------------------------------------- Kademeli seçim (helpers/poKademe)

    const satiriDegistir = (anahtar: string, degisim: (s: AtamaSatiri) => AtamaSatiri) => {
        setSatirlar(satirlar.map(x => x.anahtar == anahtar ? degisim(x) : x));
    }

    const musteriSecildi = (s: AtamaSatiri, v: CustomSingleSelectModel | undefined) =>
        satiriDegistir(s.anahtar, x => musteriSec(poSatirlari, x, v?.value));

    const bolumSecildi = (s: AtamaSatiri, v: CustomSingleSelectModel | undefined) =>
        satiriDegistir(s.anahtar, x => bolumSec(poSatirlari, x, v?.value));

    const poSecildi = (s: AtamaSatiri, v: CustomSingleSelectModel | undefined) =>
        satiriDegistir(s.anahtar, x => poSec(poSatirlari, x, v?.value));

    const poNoSecildi = (s: AtamaSatiri, v: CustomSingleSelectModel | undefined) =>
        satiriDegistir(s.anahtar, x => ({ ...x, companyPoId: v?.value }));

    const secim = (secenekler: CustomSingleSelectModel[], value: string | undefined) => value ? secenekler.find(x => x.value == value) : undefined;
    const listeOf = (secenekler: CustomSingleSelectModel[]) => () => Promise.resolve({ isSuccess: true, data: secenekler });

    // ---------------------------------------------------------------- Satırlar

    /** Son satırın saatini 0,25 adımla ikiye böler; yeni satır boş PO ile eklenir. */
    const satiriBol = () => {
        var son = satirlar[satirlar.length - 1];

        if (!son)
            return;

        var saat = saatSayi(son.saat);
        var yarim = Math.floor(saat / 2 * 4) / 4;

        if (yarim < 0.25) {
            toast.error('Bölmek için son satırda en az 0,5 saat olmalı.');
            return;
        }

        setSatirlar([
            ...satirlar.slice(0, satirlar.length - 1),
            { ...son, saat: String(ceyrek(saat - yarim)) },
            { anahtar: yeniAnahtar(), saat: String(yarim) }
        ]);
    }

    const satiriSil = (anahtar: string) => setSatirlar(satirlar.filter(x => x.anahtar != anahtar));

    const toplam = Math.round(satirlar.reduce((t, s) => t + saatSayi(s.saat), 0) * 10000) / 10000;
    const toplamTamam = secili != undefined && Math.abs(toplam - hedef) < 0.001;
    const hazir = toplamTamam && satirlar.length > 0 && satirlar.every(s => s.companyPoId && saatSayi(s.saat) > 0 && ceyrekMi(saatSayi(s.saat)));

    const ata = async () => {
        if (!secili || !hazir)
            return;

        var poIdleri = satirlar.map(x => x.companyPoId);

        if (new Set(poIdleri).size != poIdleri.length) {
            toast.error('Aynı Müşteri · Bölüm · PO iki satırda seçilmiş; saatleri tek satırda birleştirin.');
            return;
        }

        try {
            dispatch(storeActivityIndicator(true));

            var response = await MesaiAtamaService.ata({
                mesaiKaydiId: secili.mesaiKaydiId,
                satirlar: satirlar.map(x => ({ companyPoId: x.companyPoId!, dakika: Math.round(saatSayi(x.saat) * 60) }))
            });

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                dispatch(storeActivityIndicator(false));
                return;
            }

            var siparisler = response.data ?? [];
            toast.success('Atandı. ' + (siparisler.length > 0 ? 'Sipariş: ' + siparisler.map(x => x.refNo).join(', ') : 'Sipariş oluşturuldu.'));

            await getBekleyenler();
            await getAtananlar();
        } catch (error: any) {
            toast.error(error);
            dispatch(storeActivityIndicator(false));
        }
    }

    const geriAl = (mesaiKaydiId: string) => async () => {
        try {
            dispatch(storeActivityIndicator(true));

            var response = await MesaiAtamaService.geriAl(mesaiKaydiId);

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                dispatch(storeActivityIndicator(false));
                return;
            }

            toast.success('Atama geri alındı; kayıt yeniden bekleyenlere alındı.');

            await getBekleyenler();
            await getAtananlar();
        } catch (error: any) {
            toast.error(error);
            dispatch(storeActivityIndicator(false));
        }
    }

    const siparisLinkleri = (l: MesaiSiparisi[] | null | undefined) => (l ?? []).map(s =>
        <a key={s.ambarId} className='btn btn-sm btn-light-primary py-1 px-2 me-1 mb-1' onClick={() => navigate('/ambar/listadd?id=' + s.ambarId)}>{s.refNo}</a>
    );

    return (
        <React.Fragment>
            <ToolbarWrapper filters={<React.Fragment>
                <RefreshButton onClick={yenile} />
                <div style={{ flex: 1 }}></div>
            </React.Fragment>} />

            <PageTitle breadcrumbs={[{
                title: pageName.split('>').slice(0, pageName.split('>').length - 1).join(' > ') + ' > ',
                isActive: false,
                path: ''
            }]}>{(pageName.split('>')[pageName.split('>').length - 1])}</PageTitle>

            <Content>
                {/* ---------------------------------------------------------------- Bekleyenler */}
                <KTCard className='ebbe-card mb-3'>
                    <KTCardBody>
                        <h4 className="mb-2 text-black">
                            Bekleyenler
                            {bekleyenler.length > 0 && <span className='badge badge-light-warning ebbe-status-badge ms-3'>{bekleyenler.length} bekliyor</span>}
                        </h4>
                        <div className="text-muted fs-7 mb-5">Hiçbir ek mesai talebine yerleşmeyen Lojistik ek mesai buraya düşer; atama yapılmadan sipariş oluşmaz. Satıra tıklayıp aşağıda atayın.</div>

                        <div className='table-responsive'>
                            <table id='kt_table_users' className='table align-middle table-row-dashed dataTable no-footer ebbe-compact-table'>
                                <thead>
                                    <tr className='text-start text-muted fw-bolder text-uppercase'>
                                        <th>Tarih</th>
                                        <th>Personel</th>
                                        <th>QR Noktası</th>
                                        <th>Giriş – Çıkış</th>
                                        <th className='custom-text-right'>Ek Mesai</th>
                                        <th className='custom-text-right'>Sipariş Saati</th>
                                        <th>Durum</th>
                                    </tr>
                                </thead>
                                <tbody className='text-gray-600 fw-bold'>
                                    {
                                        bekleyenler.length == 0 && (
                                            <tr>
                                                <td className="text-center" colSpan={99}>Bekleyen ek mesai yok.</td>
                                            </tr>
                                        )
                                    }
                                    {
                                        bekleyenler.map(item =>
                                            <tr key={item.mesaiKaydiId} className='actionIcon' onClick={() => setSeciliId(item.mesaiKaydiId)} style={{ background: item.mesaiKaydiId == seciliId ? 'rgba(27, 132, 255, 0.08)' : undefined }}>
                                                <td>{trTarih(item.tarih)}</td>
                                                <td>{item.kisiAdi}</td>
                                                <td>{item.noktaAdi}</td>
                                                <td>{item.girisSaati} – {item.cikisSaati ?? ''}</td>
                                                <td className='custom-text-right' style={{ color: '#7239EA' }}>{saatDakika(item.ekDk)}</td>
                                                <td className='custom-text-right'>{ondalikSaat(item.siparisDk)}</td>
                                                <td>
                                                    {
                                                        item.durum == 'YENIDEN'
                                                            ? <span className='badge badge-light-danger ebbe-status-badge'>YENIDEN · Ek mesai değişti, yeniden ata</span>
                                                            : <span className='badge badge-light-warning ebbe-status-badge'>Atama bekliyor</span>
                                                    }
                                                </td>
                                            </tr>
                                        )
                                    }
                                </tbody>
                            </table>
                        </div>
                    </KTCardBody>
                </KTCard>

                {/* ---------------------------------------------------------------- Atama kutusu */}
                {
                    secili && (
                        <KTCard className='ebbe-card mb-3'>
                            <KTCardBody>
                                <h4 className="mb-5 text-black">Ata · {secili.kisiAdi} · {trTarih(secili.tarih)} · {ondalikSaat(secili.siparisDk)} saat</h4>

                                <div className='table-responsive'>
                                    <table id='kt_table_users' className='table align-middle table-row-dashed dataTable no-footer'>
                                        <thead>
                                            <tr className='text-start text-muted fw-bolder text-uppercase'>
                                                <th style={{ minWidth: 200 }}>Müşteri</th>
                                                <th style={{ minWidth: 160 }}>Bölüm</th>
                                                <th style={{ minWidth: 160 }}>PO</th>
                                                <th style={{ minWidth: 140 }}>PO No</th>
                                                <th className='custom-text-right' style={{ width: 120 }}>Saat</th>
                                                <th style={{ width: 1 }}></th>
                                            </tr>
                                        </thead>
                                        <tbody className='text-gray-600 fw-bold'>
                                            {
                                                satirlar.map(s => {
                                                    var mS = musteriSecenekleri(poSatirlari), bS = bolumSecenekleri(poSatirlari, s), pS = poSecenekleri(poSatirlari, s), nS = poNoSecenekleri(poSatirlari, s);

                                                    return (
                                                        <tr key={s.anahtar}>
                                                            <td><CustomSingleSelect selectedValue={secim(mS, s.musteri)} onChange={(v) => musteriSecildi(s, v)} onMenuOpen={listeOf(mS)} labelName='label' valueName='value' menuPortal /></td>
                                                            <td><CustomSingleSelect disabled={!s.musteri} selectedValue={secim(bS, s.bolum)} onChange={(v) => bolumSecildi(s, v)} onMenuOpen={listeOf(bS)} labelName='label' valueName='value' menuPortal /></td>
                                                            <td><CustomSingleSelect disabled={!s.bolum} selectedValue={secim(pS, s.po)} onChange={(v) => poSecildi(s, v)} onMenuOpen={listeOf(pS)} labelName='label' valueName='value' menuPortal /></td>
                                                            <td><CustomSingleSelect disabled={!s.po} selectedValue={secim(nS, s.companyPoId)} onChange={(v) => poNoSecildi(s, v)} onMenuOpen={listeOf(nS)} labelName='label' valueName='value' menuPortal /></td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    step="0.25"
                                                                    min="0"
                                                                    className="form-control form-control-solid text-end"
                                                                    style={{ border: '1px solid #F1F1F4' }}
                                                                    value={s.saat}
                                                                    onChange={(e) => satiriDegistir(s.anahtar, x => ({ ...x, saat: e.target.value }))}
                                                                />
                                                            </td>
                                                            <td>
                                                                {
                                                                    satirlar.length > 1 && (
                                                                        <button className='btn btn-sm btn-icon btn-light-danger' title='Satırı sil' onClick={() => satiriSil(s.anahtar)}>
                                                                            <i className='fa fa-trash' />
                                                                        </button>
                                                                    )
                                                                }
                                                            </td>
                                                        </tr>
                                                    )
                                                })
                                            }
                                        </tbody>
                                        <tfoot>
                                            <tr className="ebbe-dip-toplam">
                                                <td colSpan={4}>Toplam / atanacak</td>
                                                <td className={'custom-text-right ' + (toplamTamam ? 'text-success' : 'text-danger')}>
                                                    {toplam.toLocaleString('tr-TR', { maximumFractionDigits: 2 })} / {hedef.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}
                                                </td>
                                                <td></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>

                                <Box flexDirection="row" alignItems="center" className="mt-3">
                                    <button className='btn btn-sm btn-light-primary me-3' onClick={satiriBol}>
                                        <i className='fa fa-columns me-1' />
                                        Satırı böl
                                    </button>

                                    {
                                        menu?.canInsert && (
                                            <button className='btn btn-sm btn-success me-3' disabled={!hazir} onClick={ata}>
                                                <i className='fa fa-check me-1' />
                                                Ata ve sipariş oluştur
                                            </button>
                                        )
                                    }

                                    <span className='text-muted fs-7'>Saatler 0,25 adımlı; toplam atanacak saate eşit ve her satırda PO No seçili olmalı.</span>
                                </Box>
                            </KTCardBody>
                        </KTCard>
                    )
                }

                {/* ---------------------------------------------------------------- Atananlar */}
                <KTCard className='ebbe-card mb-3'>
                    <KTCardBody>
                        <Box flexDirection="row" alignItems="center" className="mb-5">
                            <h4 className="mb-0 text-black" style={{ flex: 1 }}>Atananlar</h4>

                            <input type='date' title='Başlangıç' className='form-control form-control-sm form-control-solid' style={{ width: 140 }} value={baslangic} onChange={(e) => e.target.value && setBaslangic(e.target.value)} />
                            <span className='mx-1 text-muted'>–</span>
                            <input type='date' title='Bitiş' className='form-control form-control-sm form-control-solid' style={{ width: 140 }} value={bitis} onChange={(e) => e.target.value && setBitis(e.target.value)} />
                        </Box>

                        <div className='table-responsive'>
                            <table id='kt_table_users' className='table align-middle table-row-dashed dataTable no-footer ebbe-compact-table'>
                                <thead>
                                    <tr className='text-start text-muted fw-bolder text-uppercase'>
                                        <th>Tarih</th>
                                        <th>Personel</th>
                                        <th>QR Noktası</th>
                                        <th>Atama</th>
                                        <th>Atayan</th>
                                        <th>Sipariş</th>
                                        <th style={{ width: 1 }}></th>
                                    </tr>
                                </thead>
                                <tbody className='text-gray-600 fw-bold'>
                                    {
                                        atananlar.length == 0 && (
                                            <tr>
                                                <td className="text-center" colSpan={99}>Bu aralıkta atama yok.</td>
                                            </tr>
                                        )
                                    }
                                    {
                                        atananlar.map(item =>
                                            <tr key={item.mesaiKaydiId}>
                                                <td>{trTarih(item.tarih)}</td>
                                                <td>{item.kisiAdi}</td>
                                                <td>{item.noktaAdi}</td>
                                                <td>
                                                    {(item.satirlar ?? []).map((s, i) => <div key={i}>{s.etiket} · {ondalikSaat(s.dakika)} sa</div>)}
                                                </td>
                                                <td>{item.atayan}{item.atamaZamani ? ' - ' + item.atamaZamani : ''}</td>
                                                <td>{siparisLinkleri(item.siparisler)}</td>
                                                <td>
                                                    {
                                                        menu?.canDelete && (
                                                            <button className='btn btn-sm btn-light-danger py-1 px-3 text-nowrap' onClick={() => dispatch(storeAreYouSureModalCallback(geriAl(item.mesaiKaydiId)))}>
                                                                Geri al
                                                            </button>
                                                        )
                                                    }
                                                </td>
                                            </tr>
                                        )
                                    }
                                </tbody>
                            </table>
                        </div>

                        <div className="text-muted fs-7 mt-3">
                            "Geri al" faturalanmamış siparişten satırı çıkarır ve kaydı yeniden bekleyenlere alır. Çıkış düzeltmesi ya da çalışma saati değişikliği ek mesaiyi değiştirirse atama "yeniden ata" durumuna düşer.
                        </div>
                    </KTCardBody>
                </KTCard>
            </Content>
        </React.Fragment>
    )
}

export default MesaiAtama;
